import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { useApi, fmtMoney, fmtTime, errMsg } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { TAG_TYPES } from "../../lib/constants";
import { PayInfo } from "../../components/PayMethod";
import { PageHeader, Empty, OrderStatus } from "../../components/common";

function PayPalClaim({ o }) {
  const api = useApi();
  const [ref, setRef] = useState("");
  const [done, setDone] = useState(false);
  const send = async () => {
    try { await api.post(`/groups/${o.group_id}/orders/${o.id}/payment-claim`, { reference: ref }); setDone(true); toast.success("Thanks! We'll confirm your PayPal payment shortly."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (done) return <div className="mt-3 text-xs text-sky-300" data-testid="paypal-claim-done">Payment reported · waiting for confirmation</div>;
  return (
    <div className="mt-3 flex gap-2" data-testid="paypal-claim"><Input className="h-9" placeholder="PayPal transaction ID" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="paypal-claim-input" />
      <Button size="sm" disabled={ref.trim().length < 6} onClick={send} data-testid="paypal-claim-btn">I've paid</Button></div>
  );
}

export const Invoice = ({ o, canClaim = false }) => (
  <div className="panel p-5" data-testid={`invoice-${o.invoice_no}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><div className="overline">Invoice</div><div className="font-mono font-semibold text-lg">{o.invoice_no}</div><div className="text-xs text-slate-400">{fmtTime(o.created_at)} · {o.group_name}</div></div>
      <div className="text-right"><OrderStatus value={o.status} testid={`invoice-status-${o.invoice_no}`} /><div className="font-display font-black text-2xl mt-2" data-testid={`invoice-total-${o.invoice_no}`}>{fmtMoney(o.total, o.currency)}</div></div>
    </div>
    <div className="mt-4 text-sm space-y-1">
      {o.items.map((i, k) => <div key={k} className="flex justify-between text-slate-300"><span>{i.quantity} × {i.name || TAG_TYPES[i.tag_type]} ({i.colour}){i.person_name ? ` · for ${i.person_name}` : i.print_names ? " · names printed" : ""}
        {i.gift && <span className="block text-xs text-slate-400" data-testid={`invoice-gift-${k}`}>Gift to {i.gift.recipient_name || i.gift.whatsapp} ({i.gift.whatsapp}){i.gift.message ? ` · “${i.gift.message}”` : ""} · {i.gift.sent ? `WhatsApp ${i.gift.sent}` : "sends when paid"}</span>}</span>
        <span>{fmtMoney(i.amount, o.currency)}</span></div>)}
      {o.subscription_fee > 0 && <div className="flex justify-between text-slate-300"><span>Subscription</span><span>{fmtMoney(o.subscription_fee, o.currency)}</span></div>}
    </div>
    {o.status === "pending_payment" && <PayInfo method={o.payment_method} className="mt-4" />}
    {o.payer_reference && <div className="mt-3 text-xs text-sky-300" data-testid={`invoice-payer-ref-${o.invoice_no}`}>Payer reported payment · reference {o.payer_reference} · waiting for confirmation</div>}
    {canClaim && o.status === "pending_payment" && o.payment_method === "paypal" && !o.payer_reference && <PayPalClaim o={o} />}
  </div>
);

const RenewNow = ({ group }) => {
  const days = group.subscription_until ? (new Date(group.subscription_until) - Date.now()) / 86400000 : null;
  const show = ["admin", "super"].includes(group.my_role) && (group.pages_locked || (["trial", "active"].includes(group.sub_state) && days != null && days <= 14));
  if (!show) return null;
  return <Button asChild className="bg-red-600 hover:bg-red-500 text-white"><Link to={`/app/checkout?g=${group.id}&renew=1`} data-testid="renew-now-btn">Renew now</Link></Button>;
};

export default function Orders() {
  const { group } = useOutletContext();
  const api = useApi();
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get(`/groups/${group.id}/orders`).then((r) => setOrders(r.data)); }, [api, group.id]);
  return (
    <div data-testid="orders-page">
      <PageHeader overline={{ trial: `Free trial until ${fmtTime(group.subscription_until)}`, active: `Subscription active until ${fmtTime(group.subscription_until)}`, free: "Free plan", paused: "Subscription paused" }[group.sub_state] || "No active subscription"} title="Orders & invoices">
        <RenewNow group={group} /></PageHeader>
      {group.pages_locked && <div className="panel p-4 mb-6 border-red-500/60 text-sm text-red-200" data-testid="sub-locked-warning">
        Your subscription has ended. Emergency pages for this group are locked until your renewal payment is confirmed.</div>}
      {orders && orders.length === 0 && <Empty testid="orders-empty">No orders yet. Order tags from the Tags & QR tab.</Empty>}
      <div className="grid lg:grid-cols-2 gap-4">{(orders || []).map((o) => <Invoice key={o.id} o={o} canClaim={["admin", "super"].includes(group.my_role)} />)}</div>
    </div>
  );
}
